import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfUsePage() {
  return (
    <div className="home-page">
      <nav className="home-nav" aria-label="Home navigation">
        <div className="home-nav__inner">
          <Link to="/"><img src="/logo.png" alt="ProZpects" className="home-nav__logo" /></Link>
          <div className="home-nav__links">
            <Link to="/login" className="home-nav__link home-nav__link--login">Login</Link>
            <Link to="/register" className="home-nav__link home-nav__link--signup">Sign Up</Link>
          </div>
        </div>
      </nav>

      <main className="privacy-policy">
        <div className="privacy-policy__inner">
          <h1>Terms of Use</h1>
          <p className="privacy-policy__effective">Effective Date: September 16, 2026</p>

          <p>
            These Terms of Use ("Terms") govern your access to and use of the ProZpects platform,
            including the website at app.prozpects.com and the ProZpects mobile applications for iOS
            and Android (collectively, the "Service"), operated by ProZpects Recruiting Solutions LLC
            ("ProZpects," "we," "us," or "our"). By using the Service, you agree to be bound by
            these Terms.
          </p>

          <h2>1. Eligibility</h2>
          <p>
            You must be at least 13 years old to use the Service. Users between the ages of 13 and
            17 must have a parent or legal guardian create and manage their account. By using the
            Service, you represent that you meet these requirements.
          </p>

          <h2>2. Accounts</h2>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and
            for all activities that occur under your account. You agree to notify us immediately at
            support@prozpects.com if you suspect any unauthorized use of your account. We reserve
            the right to terminate accounts that violate these Terms.
          </p>

          <h2>3. Subscriptions and Auto-Renewal</h2>

          <h3>Athlete Subscriptions</h3>
          <p>
            ProZpects offers auto-renewable subscription plans for athletes seeking to be visible to
            college coaches:
          </p>
          <ul>
            <li><strong>Monthly Plan</strong> — billed monthly until canceled</li>
            <li><strong>Annual Plan</strong> — billed once per year until canceled</li>
          </ul>

          <h3>Auto-Renewal</h3>
          <p>
            Subscriptions automatically renew unless canceled at least 24 hours before the end of
            the current subscription period. Your Apple ID account will be charged for renewal
            within 24 hours prior to the end of the current period.
          </p>

          <h3>Managing or Canceling Subscriptions</h3>
          <p>
            You can manage and cancel your subscription at any time through your Apple ID account
            settings: Settings → [your name] → Subscriptions. Cancellation takes effect at the end
            of the current billing period — you will retain access until then.
          </p>

          <h3>Free Trial</h3>
          <p>
            If a free trial is offered, unused portions of a free trial will be forfeited when a
            subscription is purchased.
          </p>

          <h2>4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
            <li>Post false, misleading, or fraudulent information about athletic abilities or credentials</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Attempt to access accounts, systems, or data that you are not authorized to access</li>
            <li>Use the Service to send unsolicited commercial messages (spam)</li>
            <li>Scrape, crawl, or extract data from the Service by automated means</li>
            <li>Reverse engineer or attempt to derive the source code of any part of the Service</li>
          </ul>

          <h2>5. User Content</h2>
          <p>
            You retain ownership of content you submit to the Service (photos, videos, statistics,
            etc.). By submitting content, you grant ProZpects a non-exclusive, worldwide,
            royalty-free license to store, display, and distribute that content solely for the
            purpose of operating the Service. You represent that you have the right to submit any
            content you provide and that it does not violate any third-party rights.
          </p>

          <h2>6. Coach and Athlete Relationship</h2>
          <p>
            ProZpects is a communication and discovery platform only. We do not guarantee any
            recruiting outcomes, scholarship offers, or athletic opportunities. Any interactions
            between coaches and athletes facilitated through the platform are solely between those
            parties. ProZpects is not responsible for the accuracy of coach profiles or the
            legitimacy of recruiting interest expressed through the platform.
          </p>

          <h2>7. Disclaimer of Warranties</h2>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind,
            either express or implied. ProZpects does not warrant that the Service will be
            uninterrupted, error-free, or free of viruses or other harmful components.
          </p>

          <h2>8. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, ProZpects shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising out of or related to
            your use of the Service, even if advised of the possibility of such damages. Our total
            liability shall not exceed the amount you paid us in the 12 months preceding the claim.
          </p>

          <h2>9. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your account at any time for violation of
            these Terms or for any other reason at our sole discretion. Upon termination, your right
            to use the Service ceases immediately. Provisions that by their nature should survive
            termination will survive.
          </p>

          <h2>10. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of material changes by
            posting the updated Terms on this page and updating the "Effective Date" above. Continued
            use of the Service after changes constitutes acceptance of the updated Terms.
          </p>

          <h2>11. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, without regard to its
            conflict of law principles. Any disputes shall be resolved in the courts located in
            Delaware.
          </p>

          <h2>12. Text Messaging (SMS) Program</h2>
          <p>
            By providing a mobile phone number and opting in, you agree to receive recurring
            text (SMS/MMS) messages from ProZpects related to your account and recruiting,
            including account verification codes and notifications, new-message and profile-activity
            alerts, camp and webinar registration confirmations and reminders, scheduling links,
            and two-way conversational messages from ProZpects concierge staff. Concierge messages
            may be sent as a group text that includes the athlete, their parents or guardians, and
            their assigned concierge manager; everyone in the group can see the other members' phone
            numbers.
          </p>
          <p>
            <strong>Message frequency varies. Message and data rates may apply.</strong> Reply{' '}
            <strong>STOP</strong> to any message to cancel and{' '}
            <strong>HELP</strong> for help. Carriers are not liable for delayed or undelivered
            messages. For details on how we handle mobile opt-in data — which we do not share or
            sell to any third parties or affiliates — see our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>

          <h2>13. Contact Us</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <p>
            ProZpects Recruiting Solutions LLC<br />
            Email: <a href="mailto:support@prozpects.com">support@prozpects.com</a>
          </p>
        </div>
      </main>

      <footer className="home-footer">
        <div className="home-footer__inner">
          <p>&copy; 2026 ProZpects Recruiting Solutions LLC. All rights reserved.</p>
          <p>
            <Link to="/privacy" className="home-footer__link">Privacy Policy</Link>
            {' · '}
            <Link to="/terms" className="home-footer__link">Terms of Use</Link>
            {' · '}
            <Link to="/support" className="home-footer__link">Support</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
